// Default-entry gate for cohort-only learners. Mounted by AuthGuard on the
// bare `/app` entry only. Workspace members and Architects pass straight
// through; a learner with exactly one active enrollment goes to that course;
// several enrollments get an honest chooser; a failed lookup is reported
// rather than silently diverting the learner into workspace setup.

import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { GanttSkeleton } from '@/components/loading';
import { useInstance } from '@/contexts/InstanceContext';
import { getIsArchitect } from '@/utils/isArchitectCache';
import {
  COURSE_LIST_STATE,
  courseSpacePath,
  resolveLearnerEntry,
  type LearnerEntry,
  type LearnerMembershipRow,
} from '@/lib/courseLab/learnerEntry';

type Reader = (table: string) => {
  select: (c: string) => {
    eq: (c: string, v: unknown) => Promise<{ data: unknown[] | null; error: { message: string } | null }>;
  };
};

async function fetchEntry(userId: string, hasWorkspace: boolean) {
  const isArchitect = await getIsArchitect(userId).catch(() => false);
  if (isArchitect || hasWorkspace) {
    return { entry: resolveLearnerEntry({ isArchitect, hasWorkspace, memberships: [] }), titles: {} };
  }
  const read = (supabase as unknown as { from: Reader }).from.bind(supabase) as Reader;
  // RLS returns only the caller's own membership rows; the explicit filter is
  // belt-and-braces, never the authority.
  const { data, error } = await read('course_cohort_members')
    .select('cohort_id, state, learner_user_id')
    .eq('learner_user_id', userId);
  if (error) throw new Error('membership_lookup_failed');
  const memberships: LearnerMembershipRow[] = (Array.isArray(data) ? data : []).map((r) => {
    const row = r as Record<string, unknown>;
    return { cohortId: String(row.cohort_id ?? ''), state: String(row.state ?? '') };
  });
  // An archived membership is lifecycle-owned, not a new enrollment. Route
  // only if the own-RLS cohort read independently confirms retained visibility.
  const retained = await Promise.all(memberships.filter(m => m.state === 'archived').map(async m => {
    const { data: c, error: e } = await read('course_cohorts').select('id, title').eq('id', m.cohortId);
    if (e) throw new Error('archived_course_lookup_failed');
    return Array.isArray(c) && c.some(v => (v as Record<string, unknown>).id === m.cohortId) ? { ...m, state: 'completed' } : null;
  }));
  const entry = resolveLearnerEntry({ isArchitect, hasWorkspace, memberships: [...memberships.filter(m => m.state !== 'archived'), ...retained.filter((m): m is LearnerMembershipRow => m !== null)] });
  const titles: Record<string, string> = {};
  if (entry.kind !== 'ordinary') {
    // Titles are a courtesy; a failed title read still leaves an honest chooser.
    const ids = entry.kind === 'choose' ? entry.cohortIds : [entry.cohortId];
    await Promise.all(
      ids.map(async (id) => {
        const { data: c } = await read('course_cohorts').select('id, title').eq('id', id);
        const row = (Array.isArray(c) ? c[0] : null) as Record<string, unknown> | null;
        if (row?.title) titles[id] = String(row.title);
      }),
    );
  }
  return { entry, titles };
}

/** User whose workspace list InstanceContext last confirmed loaded (presentation only). */
let workspaceListOwner: string | null = null;

export function LearnerEntryGate({
  userId,
  active = true,
  children,
}: {
  userId: string;
  active?: boolean;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const location = useLocation();
  // Set by a course page that could not open (BF-03): list courses instead of
  // auto-routing straight back into the same unavailable course.
  const listMode =
    (location.state as { courseEntry?: string } | null)?.courseEntry === COURSE_LIST_STATE.courseEntry;
  const inst = useInstance() as { availableInstances?: unknown[]; instancesLoaded?: boolean };
  const instancesLoaded = inst.instancesLoaded !== false;
  const listed = (inst.availableInstances?.length ?? 0) > 0;
  // Bind a loaded workspace list to the user it was loaded for. During an
  // account change InstanceContext still holds the previous user's list while
  // instancesLoaded=false; only a list confirmed loaded for THIS user counts.
  if (instancesLoaded) workspaceListOwner = listed ? userId : null;
  const hasWorkspace = listed && workspaceListOwner === userId;

  const q = useQuery({
    // `course-cohort-` prefix: AuthGuard drops this on any identity change.
    queryKey: ['course-cohort-learner-entry', userId],
    // Workspace members/Architects always resolve to ordinary navigation, so
    // never query (or block) for them.
    enabled: active && instancesLoaded && !hasWorkspace,
    retry: false,
    queryFn: () => fetchEntry(userId, false),
  });

  const entry: LearnerEntry | undefined = hasWorkspace ? undefined : q.data?.entry;
  const autoRoute = active && entry?.kind === 'course' && !listMode;
  useEffect(() => {
    if (autoRoute && entry?.kind === 'course') navigate(courseSpacePath(entry.cohortId), { replace: true });
  }, [autoRoute, entry, navigate]);

  // INCIDENT FIX: InstanceContext flips instancesLoaded back to false on every
  // refreshInstances() (e.g. entering/switching an instance). Blocking on it
  // for a user who already has workspaces unmounted the whole /app tree and
  // remounted it, producing an endless loading loop. The same user's confirmed
  // workspace list passes straight through; a new user still waits.
  // Keep this wrapper mounted on both /app and /app/task/:id so selecting a
  // task cannot replace and remount the entire workspace tree. Course-entry
  // behavior remains exclusive to the bare /app route.
  if (!active || hasWorkspace) return <>{children}</>;

  if (!instancesLoaded || q.isLoading || autoRoute) {
    return <GanttSkeleton className="h-screen" />;
  }

  const checking = q.isFetching;
  const recheck = () => { setChecked(false); void q.refetch().then(() => setChecked(true)); };

  if (q.isError) {
    return (
      <div role="alert" className="mx-auto max-w-md px-6 py-16 text-center space-y-3">
        <h1 className="text-lg font-semibold">We couldn't check your course enrollment</h1>
        <p className="text-sm text-muted-foreground">
          If you joined a course, try again to go straight to it.
        </p>
        <Button disabled={checking} onClick={recheck}>{checking ? 'Checking…' : 'Try again'}</Button>
        {checked && !checking && <p role="status" className="text-sm text-muted-foreground">Still couldn't check. Try again in a moment.</p>}
      </div>
    );
  }

  if (entry && entry.kind !== 'ordinary') {
    const ids = entry.kind === 'choose' ? entry.cohortIds : [entry.cohortId];
    return (
      <div className="mx-auto max-w-md px-6 py-16 space-y-4">
        <h1 className="text-lg font-semibold">{listMode ? 'Your courses' : 'Choose a course'}</h1>
        <p className="text-sm text-muted-foreground">
          {listMode
            ? "That course couldn't be opened just now. Pick a course, or try again in a moment."
            : "You're enrolled in more than one course."}
        </p>
        <ul className="space-y-2">
          {ids.map((id) => (
            <li key={id}>
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate(courseSpacePath(id))}>
                {q.data?.titles[id] || 'Course'}
              </Button>
            </li>
          ))}
        </ul>
        {listMode && (
          <>
            <Button variant="ghost" disabled={checking} onClick={recheck}>
              {checking ? 'Checking your enrollment…' : 'Check my enrollment again'}
            </Button>
            {checked && !checking && (
              <p role="status" className="text-sm text-muted-foreground">
                Enrollment checked: {ids.length === 1 ? 'you are enrolled in the course above' : `you are enrolled in ${ids.length} courses above`}. Select it to open.
              </p>
            )}
          </>
        )}
      </div>
    );
  }

  return <>{children}</>;
}
